import React from 'react';
import { Link } from 'react-router-dom';
import { summarise } from '../lib/discovery';

export const STATE_LABEL = {
  following: 'Following',
  available: 'Not following',
  subscription: 'Subscription only',
  'unsupported-lexicon': 'Lexicon unusable',
  'no-lexicon': 'No follow record',
  'unknown-appview': 'Unknown appview',
};

export function describeBatch(batch) {
  if (!batch) return '';

  if (batch.finished) {
    return batch.failed
      ? `Finished: ${batch.done - batch.failed} followed, ${batch.failed} failed. Each failure is explained in its row.`
      : `Finished: followed on all ${batch.total} appviews.`;
  }

  return `Following on ${batch.done} of ${batch.total} appviews…`;
}

/**
 * Presentational results surface. `readOnly` hides every write control and
 * explains what signing in would add.
 */
export default function Results({
  result,
  visibleRows,
  onlyProfiles,
  onToggleOnlyProfiles,
  pending,
  onFollowAll,
  onFollow,
  onUnfollow,
  busy = {},
  rowErrors = {},
  batch = null,
  readOnly = false,
}) {
  const counts = summarise(result.rows);
  const stats = `${counts.withProfile} appview profile${counts.withProfile === 1 ? '' : 's'} across ${result.collectionCount} record types · ${counts.followable} followable · ${counts.following} already following`;

  return (
    <section aria-labelledby="results-heading">
      <div className="lookup-summary">
        <h2 id="results-heading" className="lookup-title">
          {result.displayName ? `${result.displayName} · ` : ''}
          {result.account.handle}
        </h2>
        <p className="lookup-stats">{stats}</p>
        <p className="lookup-meta">
          Library {result.mapMeta.library.handle}: {result.mapMeta.authorityCount} known appviews,
          built {new Date(result.mapMeta.generatedAt).toISOString().slice(0, 10)}
        </p>
      </div>

      <div className="controls">
        {readOnly ? (
          <Link className="cta-link" to="/login">
            Sign in to follow
          </Link>
        ) : (
          <button
            type="button"
            onClick={onFollowAll}
            disabled={!pending.length || Boolean(batch && !batch.finished)}
          >
            {pending.length
              ? `Follow on ${pending.length} appview${pending.length === 1 ? '' : 's'}`
              : 'Nothing left to follow'}
          </button>
        )}
        <label className="check">
          <input type="checkbox" checked={onlyProfiles} onChange={onToggleOnlyProfiles} />
          <span>Only show appviews with a profile record</span>
        </label>
      </div>

      {visibleRows.length === 0 ? (
        <div className="notice notice-info">
          This account has no per-appview profile records in the library. Turn off the filter to see
          where it holds other records.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="appview-table">
            <caption className="visually-hidden">
              Appviews the account is on, the follow record each one uses, and the current state
            </caption>
            <thead>
              <tr>
                <th scope="col">Appview</th>
                <th scope="col">Profile record</th>
                <th scope="col">Follow record</th>
                <th scope="col">State</th>
                {readOnly ? null : (
                  <th scope="col" className="cell-action">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.authority}>
                  <th scope="row" className="cell-appview">
                    <span className="nsid">{row.authority}</span>
                    {row.profile && row.profile.kind !== 'profile' ? (
                      <span className="tag">{row.profile.kind}</span>
                    ) : null}
                  </th>
                  <td>
                    {row.profile ? (
                      <span
                        className="nsid nsid-muted"
                        title={row.profileRecord?.uri || row.profile.nsid}
                      >
                        {row.profile.nsid}
                      </span>
                    ) : (
                      <span className="cell-note">no profile record</span>
                    )}
                  </td>
                  <td>
                    {row.followNsid ? (
                      <span className="nsid nsid-muted">{row.followNsid}</span>
                    ) : (
                      <span className="cell-note" title={row.blockedReason || ''}>
                        none known
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`state state-${row.state}`}>{STATE_LABEL[row.state]}</span>
                    {rowErrors[row.authority] ? (
                      <span className="cell-error" role="alert">
                        {rowErrors[row.authority]}
                      </span>
                    ) : null}
                  </td>
                  {readOnly ? null : (
                    <td className="cell-action">
                      {!row.supported ? (
                        <span className="cell-note" title={row.blockedReason || ''}>
                          {row.state === 'subscription' ? 'manual' : 'unavailable'}
                        </span>
                      ) : row.isFollowing ? (
                        <button
                          className="button-secondary"
                          type="button"
                          onClick={() => onUnfollow(row)}
                          disabled={Boolean(busy[row.authority])}
                        >
                          {busy[row.authority] === 'unfollow' ? 'Removing…' : 'Unfollow'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onFollow(row)}
                          disabled={Boolean(busy[row.authority])}
                        >
                          {busy[row.authority] === 'follow' ? 'Following…' : 'Follow'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="footnote">
        Rows are grouped by the two-label NSID authority of the records the account holds. An
        appview is only marked followable when Follower holds a real follow record type for it; a
        profile without one is reported, not guessed at.
      </p>
    </section>
  );
}
